import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';

const WithdrawECharts = ({ withdrawData, typeTotal = {} }) => {
    const activityChartRef = useRef(null);
    const distributionChartRef = useRef(null);
    const [statusCounts, setStatusCounts] = useState({
        completed: 0,
        rejected: 0,
        pending: 0
    });
    const [statusAmounts, setStatusAmounts] = useState({
        completed: 0,
        rejected: 0,
        pending: 0
    });

    let activityChartInstance = null;
    let distributionChartInstance = null;

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Process data for the activity chart
    const processActivityData = () => {
        // Create a map to store data by date
        const dataMap = {};

        // Process withdrawal data and group by date
        withdrawData.forEach(item => {
            const date = new Date(item.createdAt).toISOString().split('T')[0];

            if (!dataMap[date]) {
                dataMap[date] = {
                    date,
                    pending: 0,
                    completed: 0,
                    rejected: 0,
                    total: 0
                };
            }

            // Increment based on status
            if (item.status === 'pending') {
                dataMap[date].pending += item.amount;
            } else if (item.status === 'completed') {
                dataMap[date].completed += item.amount;
            } else if (item.status === 'rejected') {
                dataMap[date].rejected += item.amount;
            }

            // Increment total
            dataMap[date].total += item.amount;
        });

        // Convert map to array and sort by date
        return Object.values(dataMap).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    // Process data for the distribution chart
    const processDistributionData = () => {
        if (!typeTotal || Object.keys(typeTotal).length === 0) {
            return [];
        }

        return Object.entries(typeTotal).map(([type, data]) => ({
            name: type === "null" ? "Other" : type,
            value: data.totalAmount,
            count: data.count
        }));
    };

    // Calculate status statistics
    const calculateStatusStats = () => {
        const counts = withdrawData.reduce((acc, item) => {
            acc[item.status] = (acc[item.status] || 0) + 1;
            return acc;
        }, {});

        const amounts = withdrawData.reduce((acc, item) => {
            acc[item.status] = (acc[item.status] || 0) + item.amount;
            return acc;
        }, {});

        setStatusCounts({
            completed: counts.completed || 0,
            rejected: counts.rejected || 0,
            pending: counts.pending || 0
        });

        setStatusAmounts({
            completed: amounts.completed || 0,
            rejected: amounts.rejected || 0,
            pending: amounts.pending || 0
        });
    };

    // Initialize the activity chart
    const initActivityChart = () => {
        if (activityChartRef.current) {
            // Dispose of previous chart instance if it exists
            if (activityChartInstance) {
                activityChartInstance.dispose();
            }

            // Initialize new chart instance
            activityChartInstance = echarts.init(activityChartRef.current);

            // Process data
            const processedData = processActivityData();

            if (processedData.length === 0) {
                return;
            }

            // Extract dates for x-axis
            const dates = processedData.map(item => item.date);

            // Prepare series data
            const completedData = processedData.map(item => item.completed);
            const pendingData = processedData.map(item => item.pending);
            const rejectedData = processedData.map(item => item.rejected);

            // Configure chart options
            const option = {
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderColor: '#e2e8f0',
                    borderWidth: 1,
                    padding: 10,
                    textStyle: {
                        color: '#2d3748',
                        fontSize: 12
                    },
                    formatter: function (params) {
                        const date = new Date(params[0].axisValue).toLocaleDateString();
                        let result = `<div style="font-weight: bold; margin-bottom: 5px;">${date}</div>`;

                        params.forEach(param => {
                            const color = param.color;
                            const name = param.seriesName;
                            const value = param.value;
                            result += `<div style="display: flex; align-items: center; margin: 3px 0;">
                                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-right: 8px;"></span>
                                <span style="margin-right: 5px; font-weight: 500;">${name}:</span>
                                <span>${formatCurrency(value)}</span>
                            </div>`;
                        });

                        return result;
                    }
                },
                legend: {
                    data: ['Completed', 'Pending', 'Rejected'],
                    bottom: 0,
                    icon: 'roundRect',
                    itemWidth: 12,
                    itemHeight: 12,
                    textStyle: {
                        color: '#718096'
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '3%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: true,
                    data: dates,
                    axisLine: {
                        lineStyle: {
                            color: '#e2e8f0'
                        }
                    },
                    axisLabel: {
                        color: '#718096',
                        formatter: function (value) {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                        }
                    },
                    axisTick: {
                        alignWithLabel: true
                    }
                },
                yAxis: {
                    type: 'value',
                    axisLine: {
                        show: false
                    },
                    axisTick: {
                        show: false
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#f0f0f0',
                            type: 'dashed'
                        }
                    },
                    axisLabel: {
                        color: '#718096',
                        formatter: function (value) {
                            return '₹' + value;
                        }
                    }
                },
                series: [
                    {
                        name: 'Completed',
                        type: 'bar',
                        stack: 'total',
                        emphasis: {
                            focus: 'series'
                        },
                        itemStyle: {
                            color: '#48bb78',
                            borderRadius: [4, 4, 0, 0]
                        },
                        data: completedData,
                        barMaxWidth: '50%',
                        barCategoryGap: '30%',
                        animationDelay: function (idx) {
                            return idx * 50;
                        }
                    },
                    {
                        name: 'Pending',
                        type: 'bar',
                        stack: 'total',
                        emphasis: {
                            focus: 'series'
                        },
                        itemStyle: {
                            color: '#f6ad55',
                            borderRadius: [4, 4, 0, 0]
                        },
                        data: pendingData,
                        barMaxWidth: '50%',
                        barCategoryGap: '30%',
                        animationDelay: function (idx) {
                            return idx * 50 + 100;
                        }
                    },
                    {
                        name: 'Rejected',
                        type: 'bar',
                        stack: 'total',
                        emphasis: {
                            focus: 'series'
                        },
                        itemStyle: {
                            color: '#f56565',
                            borderRadius: [4, 4, 0, 0]
                        },
                        data: rejectedData,
                        barMaxWidth: '50%',
                        barCategoryGap: '30%',
                        animationDelay: function (idx) {
                            return idx * 50 + 200;
                        }
                    }
                ],
                animationEasing: 'elasticOut',
                animationDelayUpdate: function (idx) {
                    return idx * 5;
                }
            };

            // Set options and render chart
            activityChartInstance.setOption(option);
        }
    };

    // Initialize the distribution chart
    const initDistributionChart = () => {
        if (distributionChartRef.current && typeTotal && Object.keys(typeTotal).length > 0) {
            // Dispose of previous chart instance if it exists
            if (distributionChartInstance) {
                distributionChartInstance.dispose();
            }

            // Initialize new chart instance
            distributionChartInstance = echarts.init(distributionChartRef.current);

            // Process data
            const distributionData = processDistributionData();

            if (distributionData.length === 0) {
                return;
            }

            // Colors for different payment types
            const colors = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

            // Configure chart options
            const option = {
                title: {
                    text: 'Payment Type Distribution',
                    left: 'center',
                    top: 0,
                    textStyle: {
                        color: '#2d3748',
                        fontSize: 16,
                        fontWeight: 'normal'
                    }
                },
                tooltip: {
                    trigger: 'item',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderColor: '#e2e8f0',
                    borderWidth: 1,
                    padding: 10,
                    textStyle: {
                        color: '#2d3748',
                        fontSize: 12
                    },
                    formatter: function (params) {
                        const total = distributionData.reduce((sum, item) => sum + item.value, 0);
                        const percentage = ((params.value / total) * 100).toFixed(1);

                        return `<div style="font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                            <div style="display: flex; align-items: center; margin: 3px 0;">
                                <span style="margin-right: 5px; font-weight: 500;">Amount:</span>
                                <span>${formatCurrency(params.value)}</span>
                            </div>
                            <div style="display: flex; align-items: center; margin: 3px 0;">
                                <span style="margin-right: 5px; font-weight: 500;">Count:</span>
                                <span>${params.data.count} transactions</span>
                            </div>
                            <div style="display: flex; align-items: center; margin: 3px 0;">
                                <span style="margin-right: 5px; font-weight: 500;">Percentage:</span>
                                <span>${percentage}%</span>
                            </div>`;
                    }
                },
                legend: {
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    itemWidth: 12,
                    itemHeight: 12,
                    textStyle: {
                        color: '#718096'
                    },
                    formatter: function (name) {
                        const item = distributionData.find(d => d.name === name);
                        if (item) {
                            return `${name} (${item.count})`;
                        }
                        return name;
                    }
                },
                series: [
                    {
                        name: 'Payment Type',
                        type: 'pie',
                        radius: ['40%', '70%'],
                        center: ['40%', '50%'],
                        avoidLabelOverlap: true,
                        itemStyle: {
                            borderRadius: 4,
                            borderColor: '#fff',
                            borderWidth: 2
                        },
                        label: {
                            show: false
                        },
                        emphasis: {
                            label: {
                                show: true,
                                fontSize: 14,
                                fontWeight: 'bold'
                            }
                        },
                        labelLine: {
                            show: false
                        },
                        data: distributionData,
                        color: colors
                    }
                ],
                animationEasing: 'elasticOut'
            };

            // Set options and render chart
            distributionChartInstance.setOption(option);
        }
    };

    // Handle window resize
    const handleResize = () => {
        if (activityChartInstance) {
            activityChartInstance.resize();
        }
        if (distributionChartInstance) {
            distributionChartInstance.resize();
        }
    };

    // Initialize charts when component mounts or data changes
    useEffect(() => {
        initActivityChart();
        initDistributionChart();
        calculateStatusStats();

        // Add resize event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (activityChartInstance) {
                activityChartInstance.dispose();
                activityChartInstance = null;
            }
            if (distributionChartInstance) {
                distributionChartInstance.dispose();
                distributionChartInstance = null;
            }
        };
    }, [withdrawData, typeTotal]);

    // Calculate total amounts and transactions
    const totalAmount = 306; // Fixed amount as specified
    const totalTransactions = Object.values(typeTotal).reduce((sum, type) => sum + type.count, 0) || 0;

    return (
        <div className="w-full">
            {/* Status cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-700">Completed</p>
                            <p className="text-xl font-bold text-green-800">{formatCurrency(statusAmounts.completed)}</p>
                        </div>
                        <div className="bg-green-100 p-2 rounded-lg">
                            <span className="text-green-700 font-medium">{statusCounts.completed}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-700">Pending</p>
                            <p className="text-xl font-bold text-orange-800">{formatCurrency(statusAmounts.pending)}</p>
                        </div>
                        <div className="bg-orange-100 p-2 rounded-lg">
                            <span className="text-orange-700 font-medium">{statusCounts.pending}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-red-700">Rejected</p>
                            <p className="text-xl font-bold text-red-800">{formatCurrency(statusAmounts.rejected)}</p>
                        </div>
                        <div className="bg-red-100 p-2 rounded-lg">
                            <span className="text-red-700 font-medium">{statusCounts.rejected}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity chart */}
            <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-main-font">Redeem Activity</h3>
                    <div className="text-sm text-main-subfont">
                        {withdrawData.length} redeem transactions
                    </div>
                </div>

                <div className="h-[300px]">
                    {withdrawData.length > 0 ? (
                        <div ref={activityChartRef} className="w-full h-full" />
                    ) : (
                        <div className="flex items-center justify-center h-full border border-dashed border-main-border rounded-lg">
                            <p className="text-main-subfont">No Redeem data available</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment type distribution */}
            {Object.keys(typeTotal).length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-main-border p-4">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-lg font-semibold text-main-font">Payment Type Distribution</h3>
                        </div>
                        <div className="flex space-x-2">
                            <div className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-medium">
                                {totalTransactions} Transactions
                            </div>
                            <div className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-xs font-medium">
                                {Object.keys(typeTotal).length} Types
                            </div>
                        </div>
                    </div>

                    <div className="h-[300px]">
                        <div ref={distributionChartRef} className="w-full h-full" />
                    </div>

                    {/* Payment type details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
                        {Object.entries(typeTotal).map(([type, data], index) => (
                            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-300">
                                <h3 className="font-semibold text-gray-800 mb-2">{type === "null" ? "Other" : type}</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <p className="text-xs text-gray-500">Amount</p>
                                        <p className="text-sm font-medium">{formatCurrency(data.totalAmount)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Transactions</p>
                                        <p className="text-sm font-medium">{data.count}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-xs text-gray-500">Percentage</p>
                                        <p className="text-sm font-medium">
                                            {((data.totalAmount / totalAmount) * 100).toFixed(1)}% of total
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawECharts;